import { useState, useEffect, useCallback, useRef } from 'react';
import { HotbarConfig, HotbarSlotItem, createDefaultHotbarConfig } from '../game/types';
import { Socket } from 'socket.io-client';
import { playSound, SFX_KEYS } from '../game/systems/SoundManager';

export const useHotbar = (socket: Socket | null) => {
  const [hotbarConfig, setHotbarConfig] = useState<HotbarConfig>(createDefaultHotbarConfig());
  const hasLoadedRef = useRef<boolean>(false);

  // Load hotbar from server AFTER ship spawns (which means authentication is complete)
  useEffect(() => {
    if (!socket) return;

    const loadHotbar = (): void => {
      if (hasLoadedRef.current) return;
      console.log('[HOTBAR] Loading hotbar from server...');
      socket.emit('hotbar:load', (config: HotbarConfig | null) => {
        if (config) {
          console.log('[HOTBAR] Received config from server:', config.slots.map(s => s.item?.itemId || 'empty'));
          setHotbarConfig(config);
          hasLoadedRef.current = true;
        } else {
          console.warn('[HOTBAR] Server returned null config');
        }
      });
    };

    // Load when ship spawns (after authentication)
    const handleShipSpawn = (): void => {
      console.log('[HOTBAR] Ship spawned, loading hotbar...');
      loadHotbar();
    };

    // Also load on reconnection
    const handleReconnect = (): void => {
      console.log('[HOTBAR] Reconnected, reloading hotbar...');
      hasLoadedRef.current = false;
      loadHotbar();
    };

    socket.on('ship:spawn', handleShipSpawn);
    socket.on('reconnect', handleReconnect);

    // Listen for hotbar updates from server (authoritative)
    socket.on('hotbar:updated', (config: HotbarConfig) => {
      console.log('[HOTBAR] Server pushed update:', config.slots.map(s => s.item?.itemId || 'empty'));
      setHotbarConfig(config);
    });

    return () => {
      socket.off('ship:spawn', handleShipSpawn);
      socket.off('reconnect', handleReconnect);
      socket.off('hotbar:updated');
    };
  }, [socket]);

  const updateSlot = useCallback(
    (slotIndex: number, item: HotbarSlotItem | null, sourceSlotIndex?: number) => {
      if (!socket) return;

      // Validate slot index
      if (slotIndex < 0 || slotIndex >= 6) {
        console.warn(`[HOTBAR] Invalid slot index: ${slotIndex}`);
        return;
      }

      // Prevent placing the same item in multiple slots (client-side check)
      // Skip this check for slot-to-slot moves (when sourceSlotIndex is provided)
      if (item && sourceSlotIndex === undefined) {
        const existingSlot = hotbarConfig.slots.findIndex(
          (slot, idx) => idx !== slotIndex && slot.item?.itemId === item.itemId
        );
        if (existingSlot !== -1) {
          console.warn(`[HOTBAR] Item ${item.itemId} already in slot ${existingSlot}`);
          return;
        }
      }

      console.log(`[HOTBAR] Updating slot ${slotIndex} with:`, item?.itemId || 'null', sourceSlotIndex !== undefined ? `(from slot ${sourceSlotIndex})` : '');

      // Optimistic update - update local state immediately
      setHotbarConfig(prev => {
        const newSlots = [...prev.slots];
        newSlots[slotIndex] = {
          ...newSlots[slotIndex],
          item: item,
        };
        return {
          ...prev,
          slots: newSlots,
          lastUpdated: Date.now(),
        };
      });

      // Send to server with sourceSlotIndex for atomic slot-to-slot moves
      socket.emit('hotbar:updateSlot', { slotIndex, item, sourceSlotIndex }, (success: boolean) => {
        console.log(`[HOTBAR] Server response for slot ${slotIndex}:`, success);
        if (!success) {
          // Revert on server error - reload from server
          console.warn('[HOTBAR] Server rejected update, reloading...');
          socket.emit('hotbar:load', (config: HotbarConfig | null) => {
            if (config) setHotbarConfig(config);
          });
        }
      });
    },
    [socket, hotbarConfig.slots]
  );

  const useSlot = useCallback(
    (slotIndex: number) => {
      if (!socket) return;

      const slot = hotbarConfig.slots[slotIndex];
      if (!slot?.item) return;

      console.log(`[HOTBAR] Using slot ${slotIndex}:`, slot.item.itemId);

      // Slot 0 is for signature moves - emit wildcard:activate instead
      if (slotIndex === 0) {
        socket.emit('wildcard:activate');
        return;
      }

      socket.emit('hotbar:useSlot', { slotIndex }, (result: { success: boolean; message?: string; cooldownMs?: number }) => {
        console.log(`[HOTBAR] Use result for slot ${slotIndex}:`, result);
        
        // Play sound on successful use
        if (result.success) {
          playSound(SFX_KEYS.ITEM_USE);
        }
        
        // Server will send hotbar:updated with the new state
        // We only need to handle cooldown locally for immediate feedback
        if (result.success && result.cooldownMs && result.cooldownMs > 0) {
          setHotbarConfig(prev => {
            const newSlots = [...prev.slots];
            newSlots[slotIndex] = {
              ...newSlots[slotIndex],
              cooldownUntil: Date.now() + result.cooldownMs!,
            };
            return {
              ...prev,
              slots: newSlots,
            };
          });
        }
      });
    },
    [socket, hotbarConfig.slots]
  );

  return {
    hotbarConfig,
    updateSlot,
    useSlot,
  };
};
